import React, { useCallback, useEffect, useState } from "react";
import api, { fileUrl } from "../lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, ChatCircle, UserPlus, EnvelopeSimple, Gift, Broadcast, UsersThree, Microphone, Fire, Trophy, Sparkle, Star, At } from "@phosphor-icons/react";

const ICONS = {
  like: Heart, comment: ChatCircle, follow: UserPlus, message: EnvelopeSimple,
  gift: Gift, live_gift: Gift, live_start: Broadcast, room_start: UsersThree,
  voice_reaction: Microphone, subscribe: Star, mention: At,
  digest: Sparkle, milestone: Trophy, streak_reminder: Fire,
};

const COLORS = {
  like: "text-pink-400", comment: "text-papaya", follow: "text-papaya", message: "text-emerald-400",
  gift: "text-jam-gold", live_gift: "text-jam-gold", live_start: "text-red-400", room_start: "text-lignum-soft",
  voice_reaction: "text-papaya", subscribe: "text-jam-gold", mention: "text-papaya",
  digest: "text-papaya", milestone: "text-jam-gold", streak_reminder: "text-jam-gold",
};

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [claimedToday, setClaimedToday] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
      api.post("/notifications/read").catch((err) => console.error("mark-read failed", err));
      if (data.some((n) => n.type === "streak_reminder")) {
        api.get("/streak").then((r) => setClaimedToday(!!r.data.claimed_today)).catch(() => {});
      }
    } catch (err) {
      console.error("notifications load failed", err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const claimStreak = async () => {
    if (claiming) return;
    setClaiming(true);
    try {
      const { data } = await api.post("/streak/claim");
      toast.success(`🔥 Comeback bonus! Day ${data.streak} saved — +${data.reward} waves`);
      setClaimedToday(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not claim streak");
      if (e?.response?.status === 400) setClaimedToday(true);
    } finally { setClaiming(false); }
  };

  return (
    <div className="min-h-[100dvh] bg-ink pb-32 px-5 pt-8">
      <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Activity</p>
      <h1 className="text-cream text-4xl font-black tracking-tighter mt-1">Notifications</h1>

      <div className="mt-6 space-y-2" data-testid="notifications-list">
        {loading && <p className="text-cream/40 text-sm">Loading…</p>}
        {!loading && items.length === 0 && (
          <p className="text-cream/50 text-sm">All quiet. The vibes will arrive soon.</p>
        )}
        {items.map((n) => {
          const Icon = ICONS[n.type] || Heart;
          const color = COLORS[n.type] || "text-cream";
          const system = !n.actor;
          return (
            <div
              key={n.id}
              data-testid={`notif-${n.id}`}
              className={`flex items-center gap-3 p-3 rounded-2xl border ${n.read ? "bg-white/5 border-white/5" : "bg-papaya/10 border-papaya/30"}`}
            >
              <Link to={n.actor ? `/u/${n.actor.username}` : "#"} className="w-11 h-11 rounded-full overflow-hidden bg-gradient-to-br from-papaya to-sea flex items-center justify-center text-ink font-black flex-shrink-0">
                {n.actor?.avatar_path ? <img src={fileUrl(n.actor.avatar_path)} alt="" className="w-full h-full object-cover" /> : system ? <Icon size={20} weight="fill" /> : (n.actor?.display_name || "?")[0]?.toUpperCase()}
              </Link>
              <div className="flex-1 min-w-0">
                <p className="text-cream text-sm">
                  <span className="font-bold">{system ? "Giffwurldaunce" : `@${n.actor.username}`}</span> <span className="text-cream/70">{n.message}</span>
                </p>
                {n.type === "streak_reminder" && (
                  <button
                    onClick={claimStreak}
                    disabled={claiming || claimedToday}
                    data-testid={`notif-claim-streak-${n.id}`}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-jam-gold text-jam-black px-3 py-1.5 text-[10px] font-black tracking-[0.15em] uppercase disabled:opacity-50"
                  >
                    <Fire size={12} weight="fill" /> {claimedToday ? "Streak saved" : "Claim now"}
                  </button>
                )}
              </div>
              <Icon size={20} weight="fill" className={color} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
